import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Upload, Trash2, Zap, Snowflake, Loader2, ImagePlus, Lock, ArrowLeft,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "jd_admin_token";

const resolveUrl = (u) => (u.startsWith("http") ? u : `${BACKEND_URL}${u}`);
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.user);
      toast.success("Welcome back!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed. Check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <a href="/" data-testid="back-to-site" className="inline-flex items-center gap-2 font-body text-sm text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft size={15} /> Back to site
        </a>
        <div className="rounded-2xl bg-white p-8 lg:p-10 border border-white/10">
          <img src="/brand/jd-logo-full.png" alt="JD Electrical Ltd" className="h-12 w-auto mb-4" />
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink flex items-center gap-2">
            <Lock size={18} className="text-brand-blue" /> Admin login
          </h1>
          <p className="font-body text-sm text-ink/50 mt-1 mb-7">Manage your gallery photos.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="font-body text-sm font-medium text-ink/70 block mb-2">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full rounded-xl border border-black/10 px-4 py-3.5 font-body text-ink outline-none focus:border-brand-blue transition-colors" />
            </div>
            <div>
              <label className="font-body text-sm font-medium text-ink/70 block mb-2">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="w-full rounded-xl border border-black/10 px-4 py-3.5 font-body text-ink outline-none focus:border-brand-blue transition-colors" />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink py-4 font-body text-sm font-semibold text-white hover:bg-brand-blue active:scale-95 disabled:opacity-60 transition-[background-color,transform] duration-200">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [tag, setTag] = useState("Electrical");
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/gallery`);
      setPhotos(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a photo first."); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("tag", tag);
    try {
      await axios.post(`${API}/gallery`, fd, { headers: authHeaders() });
      toast.success("Photo added to your gallery!");
      setTitle(""); setTag("Electrical"); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 401) { onLogout(); return; }
      toast.error(typeof d === "string" ? d : "Upload failed.");
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers: authHeaders() });
      setPhotos((p) => p.filter((x) => x.id !== id));
      toast.success("Photo removed.");
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Could not remove photo.");
    }
  };

  return (
    <div className="min-h-screen bg-cloud">
      <header className="border-b border-black/10 bg-white sticky top-0 z-10">
        <div className="mx-auto max-w-[1200px] px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl font-extrabold tracking-tighter text-ink">JD</span>
            <span className="h-1.5 w-1.5 rounded-full bg-brand-yellow" />
            <span className="font-body text-sm text-ink/50 ml-1 hidden sm:inline">Gallery Manager</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-ink/40 hidden md:inline" data-testid="admin-email">{user?.email}</span>
            <a href="/" data-testid="view-site-link" className="font-body text-sm text-ink/60 hover:text-ink transition-colors">View site</a>
            <button onClick={onLogout} data-testid="logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2 font-body text-sm font-semibold text-ink hover:border-brand-blue transition-colors">
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-10 lg:py-14">
        <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tighter text-ink">Your work gallery</h1>
        <p className="font-body text-ink/55 mt-2">Upload photos of recent jobs — they appear instantly in the "Recent work" section of your site.</p>

        {/* Upload card */}
        <form onSubmit={upload} className="mt-8 rounded-2xl border border-black/10 bg-white p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-12 gap-5 items-end">
          <div className="lg:col-span-4">
            <label className="font-body text-sm font-medium text-ink/70 block mb-2">Photo</label>
            <input ref={fileRef} data-testid="upload-file" type="file" accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-sm text-ink/70 file:mr-3 file:rounded-full file:border-0 file:bg-ink file:px-4 file:py-2.5 file:text-white file:font-semibold file:cursor-pointer hover:file:bg-brand-blue file:transition-colors" />
          </div>
          <div className="lg:col-span-4">
            <label className="font-body text-sm font-medium text-ink/70 block mb-2">Title (optional)</label>
            <input data-testid="upload-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Bedroom AC install"
              className="w-full rounded-xl border border-black/10 px-4 py-3 font-body text-ink outline-none focus:border-brand-blue transition-colors" />
          </div>
          <div className="lg:col-span-2">
            <label className="font-body text-sm font-medium text-ink/70 block mb-2">Type</label>
            <div className="flex gap-2">
              {["Electrical", "Air Con"].map((t) => (
                <button key={t} type="button" onClick={() => setTag(t)} data-testid={`upload-tag-${t.replace(" ", "-").toLowerCase()}`}
                  className={`flex-1 flex items-center justify-center gap-1 rounded-xl border py-3 font-body text-xs font-semibold transition-colors ${
                    tag === t ? (t === "Air Con" ? "border-brand-blue bg-brand-blue text-white" : "border-brand-yellow bg-brand-yellow text-ink") : "border-black/10 text-ink hover:border-black/30"
                  }`}>
                  {t === "Air Con" ? <Snowflake size={13} /> : <Zap size={13} />}
                </button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <button type="submit" disabled={uploading} data-testid="upload-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink py-3.5 font-body text-sm font-semibold text-white hover:bg-brand-blue active:scale-95 disabled:opacity-60 transition-[background-color,transform]">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <><Upload size={15} /> Add</>}
            </button>
          </div>
        </form>

        {/* Photo grid */}
        <div className="mt-10">
          {loading ? (
            <div className="flex items-center gap-2 text-ink/50 font-body"><Loader2 size={18} className="animate-spin" /> Loading…</div>
          ) : photos.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-black/15 py-20 text-center" data-testid="empty-state">
              <ImagePlus size={32} className="mx-auto text-ink/30" />
              <p className="font-display text-xl font-bold text-ink mt-4">No photos yet</p>
              <p className="font-body text-ink/50 mt-1">Upload your first job photo above. Your site shows sample photos until you do.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photo-grid">
              {photos.map((p) => (
                <div key={p.id} data-testid={`admin-photo-${p.id}`} className="relative rounded-2xl overflow-hidden border border-black/10 aspect-[4/5] group bg-white">
                  <img src={resolveUrl(p.image_url)} alt={p.title || "Job"} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className={`absolute top-3 left-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-body text-[11px] font-semibold ${p.tag === "Air Con" ? "bg-brand-blue text-white" : "bg-brand-yellow text-ink"}`}>
                    {p.tag === "Air Con" ? <Snowflake size={11} /> : <Zap size={11} />} {p.tag}
                  </span>
                  <button onClick={() => remove(p.id)} data-testid={`delete-photo-${p.id}`} aria-label="Delete"
                    className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-red-600 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-red-600 hover:text-white transition-[opacity,background-color,color]">
                    <Trash2 size={15} />
                  </button>
                  {p.title && <p className="absolute bottom-3 left-3 right-3 font-display font-bold text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity">{p.title}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setUser(null); };

  if (checking) {
    return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="animate-spin text-white" /></div>;
  }
  return user ? <Dashboard user={user} onLogout={logout} /> : <Login onLogin={setUser} />;
}
